<script setup lang="ts">
import { type ScanChannel, type ScanRequest, scanChannelsApi, scanRequestsApi } from '@/api/entities'
import { useClientStore } from '@/stores/client'

definePage({
  meta: {
    action: 'read',
    subject: 'Review',
  },
})

const clientStore = useClientStore()

const scanRequests = ref<ScanRequest[]>([])
const scanChannels = ref<ScanChannel[]>([])
const loading = ref(false)
const createDialogOpen = ref(false)

// 테이블 헤더
const headers = [
  { title: 'ID', key: 'id' },
  { title: '채널', key: 'channel' },
  { title: '상태', key: 'status' },
  { title: '생성일', key: 'created_at' },
  { title: '작업', key: 'actions', sortable: false },
]

// 스캔 요청 목록 로드
const loadScanRequests = async () => {
  loading.value = true
  try {
    const response = await scanRequestsApi.listScanRequests()
    if (response.success && response.data) {
      const data = response.data as any

      scanRequests.value = data.data || data
    }
  }
  catch (error) {
    console.error('스캔 요청 목록 로드 실패:', error)
  }
  finally {
    loading.value = false
  }
}

// 스캔 채널 목록 로드
const loadScanChannels = async () => {
  try {
    const response = await scanChannelsApi.listScanChannels()
    if (response.success && response.data) {
      const data = response.data as any

      scanChannels.value = data.data || data
    }
  }
  catch (error) {
    console.error('스캔 채널 목록 로드 실패:', error)
  }
}

// 스캔 요청 삭제
const deleteScanRequest = async (id: number | string) => {
  if (!confirm('정말로 이 스캔 요청을 삭제하시겠습니까?'))
    return

  try {
    const response = await scanRequestsApi.deleteScanRequest(id)
    if (response.success)
      loadScanRequests()
  }
  catch (error) {
    console.error('스캔 요청 삭제 실패:', error)
  }
}

// 상태 배지 색상
const getStatusColor = (status: string) => {
  const colors: Record<string, string> = {
    pending: 'warning',
    processing: 'info',
    completed: 'success',
    failed: 'error',
  }

  return colors[status] || 'default'
}

// 초기화
onMounted(() => {
  loadScanRequests()
  loadScanChannels()
})
</script>

<template>
  <div>
    <VCard>
      <VCardTitle class="d-flex align-center justify-space-between pa-6">
        <div>
          <h4 class="text-h4">
            리뷰 스캔
          </h4>
          <p
            v-if="clientStore.selectedClient"
            class="text-body-2 mb-0 mt-1"
          >
            현재 선택된 고객사: <strong>{{ clientStore.selectedClient.name }}</strong>
          </p>
        </div>
        <VBtn
          color="primary"
          @click="createDialogOpen = true"
        >
          <VIcon
            start
            icon="tabler-plus"
          />
          새 스캔 요청
        </VBtn>
      </VCardTitle>

      <VDivider />

      <VCardText>
        <VDataTable
          :headers="headers"
          :items="scanRequests"
          :loading="loading"
        >
          <template #item.status="{ item }">
            <VChip
              :color="getStatusColor(item.status)"
              size="small"
            >
              {{ item.status }}
            </VChip>
          </template>

          <template #item.created_at="{ item }">
            {{ new Date(item.created_at).toLocaleString('ko-KR') }}
          </template>

          <template #item.actions="{ item }">
            <VBtn
              icon
              size="small"
              variant="text"
              color="error"
              @click="deleteScanRequest(item.id)"
            >
              <VIcon icon="tabler-trash" />
            </VBtn>
          </template>
        </VDataTable>
      </VCardText>
    </VCard>

    <!-- 새 스캔 요청 다이얼로그 -->
    <VDialog
      v-model="createDialogOpen"
      max-width="500"
    >
      <VCard>
        <VCardTitle class="pa-6">
          새 스캔 요청
        </VCardTitle>
        <VDivider />
        <VCardText class="pa-6">
          <AppSelect
            :items="scanChannels"
            item-title="name"
            item-value="id"
            label="채널 선택"
          />
        </VCardText>
        <VCardActions class="pa-6 pt-0">
          <VSpacer />
          <VBtn
            variant="text"
            @click="createDialogOpen = false"
          >
            취소
          </VBtn>
          <VBtn
            color="primary"
            @click="createDialogOpen = false"
          >
            요청
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
